import { presenceState, type Participant } from '@databases/presence';
import { proxy, subscribe } from 'valtio';

import { telepointerColors } from '@canvas/constants/collaborator/telepointer';
import { AI_AGENT_USER } from '@canvas/model/identity';

export const getProcessedValueKey = (fieldId: string, entryId: string): string =>
  `${fieldId}-${entryId}`;

// Read-only public interface
export interface ReadonlyUIStreamingState {
  readonly activeAiFieldCell: { fieldId: string; entryId: string } | null;
  readonly entryIdSelectedForProcessing: string | undefined;
  readonly fieldIdSelectedForProcessing: string | undefined;
  readonly fieldsAreFinishedStreaming: boolean;
  readonly isSingleFieldStreaming: boolean | undefined;
  readonly firstLayoutProcessed: boolean;
  readonly processedValues: ReadonlySet<string>;
  readonly addProcessedValue: (fieldId: string, entryId: string) => void;
  readonly currentFieldOperation: 'delete' | undefined;
  readonly currentEntryOperation: 'edit' | 'delete' | undefined;
}

// Internal mutable interface for StreamingStateManager
export interface MutableUIStreamingState {
  activeAiFieldCell: { fieldId: string; entryId: string } | null;
  entryIdSelectedForProcessing: string | undefined;
  fieldIdSelectedForProcessing: string | undefined;
  fieldsAreFinishedStreaming: boolean;
  isSingleFieldStreaming: boolean | undefined;
  firstLayoutProcessed: boolean;
  processedValues: Set<string>;
  addProcessedValue: (fieldId: string, entryId: string) => void;
  currentFieldOperation: 'delete' | undefined;
  currentEntryOperation: 'edit' | 'delete' | undefined;
}

const _internalState = proxy<MutableUIStreamingState>({
  activeAiFieldCell: null,
  entryIdSelectedForProcessing: undefined,
  fieldIdSelectedForProcessing: undefined,
  fieldsAreFinishedStreaming: false,
  isSingleFieldStreaming: undefined,
  firstLayoutProcessed: false,
  processedValues: new Set<string>(),
  addProcessedValue(fieldId: string, entryId: string) {
    this.processedValues.add(getProcessedValueKey(fieldId, entryId));
  },
  currentFieldOperation: undefined,
  currentEntryOperation: undefined
});

export const aiFirstCreationStreamingState: ReadonlyUIStreamingState = _internalState;

/**
 * INTERNAL USE ONLY
 *
 * This function provides mutable access to the streaming state.
 * Should ONLY be used by StreamingStateManager.
 *
 * @internal
 */
export function __UNSAFE_getMutableStreamingState(): MutableUIStreamingState {
  return _internalState;
}

const ROVO_PARTICIPANT = {
  identity: { ...AI_AGENT_USER, displayName: 'Rovo' },
  colorToken: telepointerColors[0].telepointerColorToken // this will be replaced by a gradient
} satisfies Participant;

export const DATABASE_BE_STREAMING_PARTICIPANT = {
  identity: {
    ...AI_AGENT_USER,
    accountId: 'database-be-streaming-agent',
    displayName: 'Rovo'
  },
  colorToken: telepointerColors[0].telepointerColorToken
} satisfies Participant;

// In test environments, Valtio may be mocked and
// "subscribe" will throw if passed a non-proxy. Wrap in try/catch so tests that mock Valtio don't crash.
try {
  subscribe(aiFirstCreationStreamingState as any, () => {
    const entryId = aiFirstCreationStreamingState.entryIdSelectedForProcessing;
    const fieldId = aiFirstCreationStreamingState.fieldIdSelectedForProcessing;

    if (entryId) {
      presenceState.addParticipant({ entryId }, ROVO_PARTICIPANT);
    } else {
      presenceState.removeParticipant(ROVO_PARTICIPANT);
    }

    if (fieldId) {
      presenceState.addParticipant({ fieldId }, ROVO_PARTICIPANT);
    }
  });
  // eslint-disable-next-line canvas/no-swallow-errors-in-catch, no-empty
} catch {}
